import { useState } from 'react';
import { useClient } from '../../../context/client';
import { useAsyncEffect } from '../../../hooks';

function getSearchResultsWithSelectedVariant(searchResults, selectedColor) {
	return searchResults.map((product) => {
		if (!selectedColor || !product.variants) return product;

		const diagramLetter = product.diagram?.charAt(0);

		// Filter the variants that match the selected color.
		const filteredVariants = product.variants
			// Will return true if any of the variant option values are color and is the selected color.
			?.filter((variant) =>
				variant?.option_values?.some(
					(value) =>
						value?.option_display_name.toLowerCase() === 'color' &&
						value?.label === selectedColor,
				),
			);

		if (!filteredVariants || filteredVariants.length <= 0) return product;

		// Filter the variants that match the price code.
		const priceCodeFilteredVariants = filteredVariants
			// Will return true if any of the variant option values are price code and is the diagram letter.
			?.filter((variant) =>
				variant?.option_values?.some(
					(value) =>
						value?.option_display_name.toLowerCase() ===
							'price code' && value?.label === diagramLetter,
				),
			);

		// If there are no matches for both color and price code, use the first one from that color.
		// I'm assuming there will every be only one match, but if there are more, we'll return the first one.
		return {
			...product,
			selectedVariant:
				priceCodeFilteredVariants[0] ?? filteredVariants[0],
		};
	});
}

export function useSearchResults(
	year,
	make,
	model,
	subModel,
	productType,
	productName,
	color,
	customerGroup,
) {
	const [searchResults, setSearchResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const client = useClient();

	useAsyncEffect(async () => {
		if (!year || !make || !model || !productType || !productName || !color)
			return setSearchResults([]);
		setIsLoading(true);

		const searchParams = {
			year,
			make,
			model,
			product_type: productType,
			customer_group_id: customerGroup,
		};

		if (subModel) searchParams.subModel = subModel;
		if (productName) searchParams.product_name = productName;
		if (color) searchParams.color = color;

		try {
			const products = await client.getProductDetails(searchParams);

			const results = products.map((product) => ({
				...product,
				quantity: 1,
				year,
				make,
				model,
				subModel,
				productType,
				productName,
				color,
				option_values: product.option_values ?? {},
				selectedAddOns: {},
			}));

			setSearchResults(
				getSearchResultsWithSelectedVariant(results, color),
			);
		} catch {
			setSearchResults([]);
		}
		setIsLoading(false);
	}, [
		year,
		make,
		model,
		subModel,
		productType,
		productName,
		color,
		customerGroup,
	]);

	return [isLoading, searchResults];
}
